<script setup>
import { computed, resolveComponent } from 'vue'

const props = defineProps({
  eyebrow: { type: String, default: undefined },
  title: { type: String, default: undefined },
  image: { type: String, default: undefined },
  alt: { type: String, default: '' },
  to: { type: [String, Object], default: undefined },
  background: { type: String, default: undefined },
  fullImage: { type: Boolean, default: false },
  // Legacy props
  reveal: { type: Boolean, default: false },
  contain: { type: Boolean, default: false },
  square: { type: Boolean, default: false },
  portrait: { type: Boolean, default: false },
})

const nuxtLink = resolveComponent('NuxtLink')
const tag = computed(() => (props.to ? nuxtLink : 'div'))
</script>

<template>
  <component
    :is="tag"
    :to="to"
    :class="[
      $style['card'],
      background && $style[`card--${background}`],
      fullImage && $style['card--full-image'],
      reveal && $style['card--reveal'],
      contain && $style['card--contain'],
      square && $style['card--square'],
      portrait && $style['card--portrait'],
    ]"
  >
    <div v-if="image || $slots.image" :class="$style['card__image']">
      <NuxtImg
        v-if="image"
        format="webp"
        :src="image"
        quality="50"
        width="400"
        loading="lazy"
        :alt="alt"
      />
      <slot v-else name="image" />
    </div>
    <div :class="$style['card__body']">
      <span v-if="eyebrow" :class="$style['card__eyebrow']">{{ eyebrow }}</span>
      <div v-if="title" :class="$style['card__title']">{{ title }}</div>
      <slot />
    </div>
    <div v-if="$slots.actions" :class="$style['card__actions']">
      <slot name="actions" />
    </div>
  </component>
</template>

<style module>
.card {
  background: var(--opacity-surface);
  border-radius: var(--border-radius);
  color: var(--text-base);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  transition: all var(--duration-micro-fast) var(--easing-transition);
  width: 100%;
}

.card[href]:hover {
  box-shadow: var(--box-shadow-elevation-3);
}

.card[href]:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--text-base);
}

.card__image {
  background: var(--dark-color-20);
  width: 100%;
}

.card__image > * {
  aspect-ratio: 16 / 9;
  object-fit: cover;
  width: 100%;
  height: 100%;
  display: block;
}

.card__body {
  padding: var(--spacing-xl);
  flex: 1;
}

.card__eyebrow {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  color: var(--primary-color);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  display: block;
  margin-bottom: var(--spacing-s);
}

.card__title {
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-medium);
  margin-bottom: var(--spacing-s);
}

.card__actions {
  padding: var(--spacing) var(--spacing-xl) var(--spacing-xl);
  display: flex;
  gap: var(--spacing);
  justify-content: flex-end;
}

.card--full-image {
  position: relative;
}

.card--full-image .card__body {
  position: absolute;
  bottom: 0;
  width: 100%;
  background: linear-gradient(180deg, rgb(0 0 0 / 0%), rgb(0 0 0 / 80%));
  text-shadow: 1px 1px 1px #000;
}

.card--dark .card__image {
  background: var(--dark-color-30);
}

.card--light .card__image {
  background: oklch(from var(--primary-color) 85% 0.03 h);
}

.card--portrait .card__image {
  aspect-ratio: 9 / 16;
}

.card--contain .card__image {
  padding: var(--spacing);
}

.card--contain .card__image > * {
  object-fit: contain;
}

.card--square .card__image {
  aspect-ratio: 1;
}

.card--reveal .card__image,
.card--reveal .card__body,
.card--reveal .card__actions,
.card:not([href]) .card__image,
.card:not([href]) .card__body,
.card:not([href]) .card__actions {
  transform: none;
  position: static;
}

/* The public pages: titles in poster capitals, and linked photos lean in on hover */
:global(:where([data-theme="stage"])) .card__title {
  font-size: clamp(1.125rem, 1rem + 0.5vw, 1.5rem);
  line-height: 1;
  letter-spacing: -0.02em;
  text-transform: uppercase;
}

:global(:where([data-theme="stage"])) .card[href] .card__image > * {
  transition: transform 600ms var(--stage-ease);
}

:global(:where([data-theme="stage"])) .card[href]:hover .card__image > * {
  transform: scale(1.04);
}

@media (prefers-reduced-motion: reduce) {
  :global(:where([data-theme="stage"])) .card[href] .card__image > * {
    transition: none;
  }
}
</style>
